import React, { forwardRef, Fragment, useImperativeHandle, useRef, useState, useEffect } from 'react';
import * as S from './button.styles';
import { useButton, useFocusRing } from 'react-aria';
import { ButtonContentProps, ButtonProps, ButtonSize } from './button.types';
import { Icon } from '@design-system/atoms/icon/Icon';
import { useButtonIconColor } from '@design-system/atoms/button/hooks';
import { Loader } from '@design-system/atoms/loader';

/**
 * Button content section
 */

const iconSizeMap = {
  small: 16,
  medium: 20,
  large: 24,
};

const ButtonContent: React.FC<ButtonContentProps> = (props) => {
  const {
    variant = 'primary',
    tone = 'default',
    startIcon: StartIconProp,
    size = 'medium',
    isLoading = false,
    isDisabled = false,
    icon: IconProp,
    endIcon,
    children,
  } = props;

  const iconColor = useButtonIconColor(variant, tone, isLoading, isDisabled);

  if (isLoading) {
    const hasIcon = Boolean(IconProp);
    return <Loader variant={hasIcon ? 'singleDot' : 'threeDots'} />;
  }

  if (IconProp) {
    return typeof IconProp === 'string' ? (
      <Icon name={IconProp} size={iconSizeMap[size]} color={iconColor} />
    ) : (
      <IconProp size={iconSizeMap[size]} color={iconColor} />
    );
  }

  return (
    <Fragment>
      {StartIconProp && (
        <>
          {typeof StartIconProp === 'string' ? (
            <Icon name={StartIconProp} size={iconSizeMap[size]} color={iconColor} />
          ) : (
            <StartIconProp weight="bold" size={iconSizeMap[size]} color={iconColor} />
          )}
        </>
      )}
      {children}
      {endIcon && <Icon name={endIcon} color={iconColor} size={iconSizeMap[size]} />}
    </Fragment>
  );
};

/**
 * Button component section
 */

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    variant = 'primary',
    tone = 'default',
    startIcon,
    size = 'medium',
    onFocus,
    onBlur,
    onClick,
    onKeyDown,
    onPress,
    value,
    isLoading,
    isDisabled,
    icon,
    endIcon,
    className,
    children,
    ...restProps
  } = props;

  const buttonRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Pass only compatible props to useButton
  const { buttonProps, isPressed } = useButton(
    {
      isDisabled,
      onPress,
      ...restProps,
    },
    buttonRef
  );

  const { isFocusVisible, focusProps } = useFocusRing({
    within: false,
    isTextInput: false,
    autoFocus: false,
  });

  // Measure content dimensions on first render and when children change
  useEffect(() => {
    if (contentRef.current && !isLoading && !dimensions.width) {
      const { offsetWidth, offsetHeight } = contentRef.current;
      setDimensions({ width: offsetWidth, height: offsetHeight });
    }
  }, [children, dimensions.width, isLoading]);

  const { onClick: ariaOnClick, ...restButtonProps } = buttonProps;
  const mergedProps = {
    ...restButtonProps,
    ...focusProps,
    onFocus: (e: React.FocusEvent<HTMLButtonElement>) => {
      buttonProps.onFocus?.(e);
      focusProps.onFocus?.(e);
      onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent<HTMLButtonElement>) => {
      buttonProps.onBlur?.(e);
      focusProps.onBlur?.(e);
      onBlur?.(e);
    },
  };

  useImperativeHandle(ref, () => buttonRef.current!);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isDisabled || isLoading) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    ariaOnClick?.(e);
    if (onClick) {
      onClick(e);
    }
  };

  // Add a keyboard handler to prevent keyboard interactions when loading
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (isLoading) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    onKeyDown?.(event);
  };

  return (
    <S.Button
      variant={variant}
      tone={tone}
      tabIndex={isLoading ? -1 : undefined}
      size={(typeof size === 'string' ? size : 'medium') as ButtonSize}
      ref={buttonRef}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      isLoading={isLoading}
      isFocusVisible={isFocusVisible}
      hasIcon={!!icon}
      disabled={isDisabled || isLoading}
      value={typeof value === 'number' ? value.toString() : value}
      data-pressed={isPressed}
      className={className}
      $width={dimensions.width}
      $height={dimensions.height}
      {...mergedProps}
      aria-disabled={isDisabled || isLoading}
      aria-busy={isLoading}
    >
      <S.ContentWrapper ref={contentRef} isHidden={isLoading}>
        <ButtonContent
          variant={variant}
          startIcon={startIcon}
          size={size}
          tone={tone}
          isLoading={false}
          isDisabled={isDisabled}
          icon={icon}
          endIcon={endIcon}
        >
          {children}
        </ButtonContent>
      </S.ContentWrapper>

      {isLoading ? (
        <S.LoadingWrapper>
          <ButtonContent
            isLoading={isLoading}
            icon={icon}
            startIcon={startIcon}
            endIcon={endIcon}
            size={size}
            variant={variant}
          >
            {children}
          </ButtonContent>
        </S.LoadingWrapper>
      ) : null}
    </S.Button>
  );
});

Button.displayName = 'Button';
